<template>
    <div class="external-propose-time-page">
        <div v-if="!isProposedTimeSent" class="page-header">
            <h1 class="page-title">{{ $t('pages.referrals-pool.index.external_propose_time_page.title') }}</h1>
            <p class="page-subtitle">{{ $t('pages.referrals-pool.index.external_propose_time_page.description') }}</p>
        </div>

        <referrals-confirmation v-if="isProposedTimeSent" heading="Success!" type="success">
            <template #content>
                <common-content-spacer height="medium">
                    <common-page-header
                        :heading="$t('pages.referrals-pool.index.external_propose_time_page.success.heading')"
                        :content="$t('pages.referrals-pool.index.external_propose_time_page.success.content')"
                        size="medium"
                        contentSpacing="medium"
                        :isCenter="true"
                        contentColor="secondary"
                    ></common-page-header>
                </common-content-spacer>
            </template>
        </referrals-confirmation>

        <common-content-wrapper v-else width="gigantic" class="external-propose-time-page__calendar-wrap">
            <div class="propose-time-calendar">
                <common-page-header
                    :heading="$t('pages.referrals-pool.index.external_propose_time_page.heading')"
                    size="small"
                    contentSpacing="medium"
                ></common-page-header>

                <availability-topnav-content
                    :currentDate="calendarDate"
                    :view="calendarView"
                    :onDateChange="changeCalendarDate"
                    :showLocationSelector="false"
                    :showManageButton="false"
                    :showViewToggle="false"
                ></availability-topnav-content>

                <div class="propose-time-calendar__send">
                    <common-button
                        :label="$t('pages.referrals-pool.index.external_propose_time_page.submit_time_slots')"
                        :isSubmittable="false"
                        :isDisabled="selectedCount < 3"
                        size="medium"
                        type="primary"
                        :isProcessing="isSending"
                        @click="submitTimeSlots()"
                    ></common-button>
                </div>

                <external-schedule
                    ref="externalSchedule"
                    :physician="externalPhysician"
                    :practice="practice"
                    :timeframe="$route.query.timeframe"
                    :createdAt="$route.query.createdAt"
                    :currentDate="calendarDate"
                ></external-schedule>
            </div>
        </common-content-wrapper>

        <confirm-slots-modal
            v-if="showConfirmSlotsModal"
            :isOpen="true"
            :selectedSlotsByDate="selectedSlotsByDate"
            :isProcessing="isSending"
            @confirm="handleConfirmSlots"
            @close="closeConfirmSlotsModal"
        ></confirm-slots-modal>

        <portal-target name="modals"></portal-target>
    </div>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex'
    import ExternalSchedule from 'components/page-elements/referrals/ExternalSchedule'
    import AvailabilityTopnavContent from 'components/layouts/app-layout/AvailabilityTopnavContent'
    import dayjs from 'dayjs'
    import ProviderDatabaseAPI from 'api/ProviderDatabaseAPI'
    import ConfirmSlotsModal from 'components/page-elements/auth/ConfirmSlotsModal.vue'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import ReferralsConfirmation from 'components/page-elements/ReferralsConfirmation.vue'

    export default {
        components: {
            ExternalSchedule,
            AvailabilityTopnavContent,
            ConfirmSlotsModal,
            ReferralsConfirmation,
        },

        data() {
            return {
                externalPhysician: {
                    id: null,
                    firstName: 'External',
                    lastName: 'Provider',
                    scheduleType: 'custom',
                    workingHours: {
                        monday: { start: '08:00', end: '17:00', isWorking: true },
                        tuesday: { start: '08:00', end: '17:00', isWorking: true },
                        wednesday: { start: '08:00', end: '17:00', isWorking: true },
                        thursday: { start: '08:00', end: '17:00', isWorking: true },
                        friday: { start: '08:00', end: '17:00', isWorking: true },
                        saturday: { start: '08:00', end: '17:00', isWorking: false },
                        sunday: { start: '08:00', end: '17:00', isWorking: false },
                    },
                    appointmentLength: 60,
                },
                practice: {},
                selectedSlotsByDate: {},
                isSending: false,
                isProposedTimeSent: false,
                referralPoolId: null,
                npiNumber: null,
                token: null,
                showConfirmSlotsModal: false,
            }
        },

        provide() {
            return {
                isReferralPool: true,
                isProposeFlow: true,
            }
        },

        computed: {
            ...mapGetters('availability', { storeSelectedSlots: 'selectedSlots', selectedCount: 'selectedCount' }),

            calendarDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date).toDate() : new Date()
            },

            calendarView() {
                return this.$route.query.view || 'monthly'
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE', 'SET_MAX_SLOTS']),

            changeCalendarDate(date) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            date: dayjs(date).format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },

            syncSelectedSlotsFromStore(slots) {
                const grouped = {}
                slots
                    .slice()
                    .sort((a, b) => dayjs(`${a.date} ${a.startTime}`) - dayjs(`${b.date} ${b.startTime}`))
                    .forEach((slot) => {
                        const entry = {
                            label: `${slot.startTime} - ${slot.endTime}`,
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        }
                        if (!grouped[slot.date]) {
                            this.$set(grouped, slot.date, [])
                        }
                        grouped[slot.date].push(entry)
                    })

                this.selectedSlotsByDate = grouped
            },

            async submitTimeSlots() {
                this.showConfirmSlotsModal = true
            },

            async handleConfirmSlots() {
                const proposedTimeSlots = []
                Object.values(this.selectedSlotsByDate).forEach((slots) => {
                    slots.forEach((slot) => {
                        proposedTimeSlots.push({
                            date: slot.date,
                            startTime: slot.startTime,
                            endTime: slot.endTime,
                        })
                    })
                })

                const request = {
                    referralPoolId: Number(this.referralPoolId),
                    token: this.token,
                    proposedTimeSlots: proposedTimeSlots,
                }
                if (this.npiNumber) {
                    request.npi = this.npiNumber
                }

                this.isSending = true

                ReferralsPoolAPI.externalProposeTimeSlots(this.referralPoolId, request)
                    .then(() => {
                        this.isProposedTimeSent = true
                    })
                    .finally(() => {
                        this.isSending = false
                        this.showConfirmSlotsModal = false
                    })
            },

            closeConfirmSlotsModal() {
                this.showConfirmSlotsModal = false
            },

            closeWindow() {
                window.close()
            },

            fetchProviderByNpi() {
                const fields = [
                    'firstName',
                    'middleName',
                    'lastName',
                    'email',
                    'phone',
                    'designation',
                    'specialities',
                    'practiceName',
                    'practiceAddress',
                ]

                ProviderDatabaseAPI.get(this.npiNumber, fields, this.referralPoolId).then((res) => {
                    this.externalPhysician = { ...this.externalPhysician, ...res }
                })
            },
        },

        watch: {
            storeSelectedSlots(slots) {
                this.syncSelectedSlotsFromStore(slots)
            },
        },

        mounted() {
            this.referralPoolId = this.$route.query.referralPoolId
            this.npiNumber = this.$route.query.npiNumber
            this.token = this.$route.query.token

            this.SET_MAX_SLOTS(3)
            this.SET_SELECT_MODE(true)

            if (!this.referralPoolId || !this.token) {
                this.$router.push({ name: 'auth.referral' })
            }

            if (this.npiNumber) {
                this.fetchProviderByNpi()
            }
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
        },
    }
</script>

<style scoped lang="scss">
    .external-propose-time-page {
        min-height: 100vh;
        background: #f8f9fa;
        padding: 40px 20px;
    }

    .external-propose-time-page__calendar-wrap {
        margin: 0 auto;
        background: #ffffff;
        border-radius: 8px;
        padding: 24px;
        box-sizing: border-box;
    }

    .page-header {
        text-align: center;
        margin-bottom: 40px;
    }

    .page-title {
        font-size: 32px;
        font-weight: 700;
        color: #212529;
        margin-bottom: 12px;
    }

    .page-subtitle {
        font-size: 16px;
        color: #6c757d;
        margin: 0;
    }
</style>
